"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Copy, Sparkles } from "lucide-react"
import { useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { useLanguage } from "@/contexts/language-context"
import { ShareButton } from "@/components/share-button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

// Define response item interface
interface ResponseItem {
  title: string;
  content: string;
  note?: string;
}

// Define parsed response content
interface ParsedResponse {
  description?: string;
  items: ResponseItem[];
}

interface ResponseCardProps {
  // Original response content
  response?: string;
  // Translation content (optional, will try to extract from response if not provided)
  translation?: string;
  // Streaming loading indicator
  isStreaming?: boolean;
}

export function ResponseCard({
  response = "",
  translation = "",
  isStreaming = false
}: ResponseCardProps) {
  const { toast } = useToast()
  const { t, currentLanguage } = useLanguage()
  const [isOriginalOpen, setIsOriginalOpen] = useState(false)

  // Function to extract translation content
  const extractTranslation = (content: string): { mainContent: string; translation: string } => {
    // Multiple translation separator patterns
    const translationPatterns = [
      "**翻译:**",
      "**翻譯:**",
      "**TRANSLATION:**",
      "**TRADUCCIÓN:**",
      "**TRADUCTION:**",
      "**ÜBERSETZUNG:**",
      /\*\*翻译:\*\*/i,
      /\*\*翻譯:\*\*/i,
      /\*\*TRANSLATION:\*\*/i
    ];

    for (const pattern of translationPatterns) {
      if (typeof pattern === 'string') {
        if (content.includes(pattern)) {
          const parts = content.split(pattern);
          if (parts.length >= 2) {
            return {
              mainContent: parts[0].trim(),
              translation: parts[1].trim()
            };
          }
        }
      } else {
        // Regular expression pattern
        const match = content.match(pattern);
        if (match) {
          const splitIndex = match.index! + match[0].length;
          return {
            mainContent: content.substring(0, match.index!).trim(),
            translation: content.substring(splitIndex).trim()
          };
        }
      }
    }

    return { mainContent: content, translation: "" };
  };

  // Extract translation content
  const { mainContent, translation: extractedTranslation } = extractTranslation(response);
  const finalTranslation = translation || extractedTranslation;

  // Intelligent response content parsing
  const parseResponse = (rawResponse: string): ParsedResponse => {
    if (!rawResponse.trim()) {
      return { items: [] };
    }

    const cleanResponse = rawResponse.trim().replace(/\r\n/g, "\n");

    const getLineForIndex = (text: string, index: number) => {
      const start = text.lastIndexOf("\n", index);
      const end = text.indexOf("\n", index);
      return text.slice(start === -1 ? 0 : start + 1, end === -1 ? text.length : end);
    };

    const normalizeContentBlock = (text: string) => {
      return text
        .split("\n")
        .map(line => line.replace(/^\s*>\s?/, ""))
        .join("\n")
        .trim();
    };

    const instructionKeywords = /(?:tap|click|press|open|navigate|go to|scroll|step|submission|app review|instructions|步骤|点击|打开|进入|前往|滚动|提交|审核|说明)/i;

    const normalizeTitle = (line: string) => {
      let title = line
        .replace(/^\s*(?:Option|选项|方案|版本|回复|建议)\s*(?:\d+|[一二三四五六七八九十]+)\s*[：:\-\.]*\s*/i, "")
        .replace(/^\s*\d{1,2}[\.)、]\s*/, "")
        .replace(/^\*\*|\*\*$/g, "")
        .replace(/^#+\s*/, "")
        .trim();
      return title;
    };

    const extractBoldTitle = (line: string) => {
      const boldMatch = line.match(/\*\*([^*]+)\*\*/);
      if (boldMatch) {
        return boldMatch[1].trim();
      }
      return "";
    };

    const parseHeaderLine = (line: string, currentIndex: number, headerCount: number) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      if (instructionKeywords.test(trimmed)) return null;

      const numberedBoldMatch = trimmed.match(/^\s*\d{1,2}[\.)、]\s*\*\*([^*]+)\*\*\s*$/);
      if (numberedBoldMatch) {
        return { index: currentIndex, title: normalizeTitle(numberedBoldMatch[1]), inlineContent: "" };
      }

      const bulletBoldMatch = trimmed.match(/^\s*[-*]\s*\*\*([^*]+)\*\*\s*$/);
      if (bulletBoldMatch) {
        return { index: currentIndex, title: normalizeTitle(bulletBoldMatch[1]), inlineContent: "" };
      }

      const boldOnlyMatch = trimmed.match(/^\s*(?:#{1,3}\s*)?\*\*([^*]+)\*\*\s*$/);
      if (boldOnlyMatch) {
        return { index: currentIndex, title: normalizeTitle(boldOnlyMatch[1]), inlineContent: "" };
      }

      const optionMatch = trimmed.match(/^\s*(?:Option|选项|方案|版本|回复|建议)\s*(?:\d+|[一二三四五六七八九十]+)\s*[：:\-\.]*\s*(.*)$/i);
      if (optionMatch) {
        const inlineContent = (optionMatch[1] || "").trim();
        const rawTitle = normalizeTitle(trimmed);
        return {
          index: currentIndex,
          title: rawTitle || `${t("interface.option")} ${headerCount + 1}`,
          inlineContent
        };
      }

      const headingMatch = trimmed.match(/^\s*#{1,3}\s*(.+)$/);
      if (headingMatch) {
        const headingTitle = normalizeTitle(headingMatch[1]);
        if (headingTitle) {
          return { index: currentIndex, title: headingTitle, inlineContent: "" };
        }
      }

      const numberedMatch = trimmed.match(/^\s*\d{1,2}[\.)、]\s*(.+)$/);
      if (numberedMatch) {
        const candidate = numberedMatch[1].trim();
        const boldTitle = extractBoldTitle(candidate);
        const endsWithColon = /[：:]$/.test(candidate);
        const isShort = candidate.length <= 40;

        if (boldTitle) {
          return { index: currentIndex, title: normalizeTitle(boldTitle), inlineContent: "" };
        }

        if (endsWithColon || isShort) {
          return { index: currentIndex, title: normalizeTitle(candidate), inlineContent: "" };
        }
      }

      return null;
    };

    // Try parsing markdown/numbered headers first
    const lines = cleanResponse.split("\n");
    const headers: { index: number; title: string; inlineContent: string }[] = [];

    lines.forEach((line, index) => {
      const header = parseHeaderLine(line, index, headers.length);
      if (!header) return;

      if (!header.title) {
        header.title = `${t("interface.option")} ${headers.length + 1}`;
      }

      headers.push(header);
    });

    if (headers.length >= 2) {
      const items: ResponseItem[] = [];
      const firstHeaderIndex = headers[0].index;
      const descriptionLines = lines.slice(0, firstHeaderIndex).join("\n").trim();

      headers.forEach((header, idx) => {
        const startLine = header.index + 1;
        const endLine = idx < headers.length - 1 ? headers[idx + 1].index : lines.length;
        const bodyLinesRaw = lines.slice(startLine, endLine).join("\n");
        const bodyLines = normalizeContentBlock(bodyLinesRaw);
        const content = [header.inlineContent, bodyLines].filter(Boolean).join("\n").trim();

        if (content) {
          items.push({
            title: header.title,
            content,
          });
        }
      });

      if (items.length >= 2) {
        return {
          description: descriptionLines || t("interface.multipleOptions"),
          items,
        };
      }
    }

    // Simplified parsing strategy: find all quoted content as copyable options
    const quotedContentPattern = /"([^"]+)"/g;
    const allQuotedMatches = [...cleanResponse.matchAll(quotedContentPattern)];

    const quotedMatches = allQuotedMatches.filter((match) => {
      const quotedContent = (match[1] || "").trim();
      if (!quotedContent) return false;

      const wordCount = quotedContent.split(/\s+/).filter(Boolean).length;
      const isLongEnough = quotedContent.length >= 6 && (quotedContent.length >= 12 || wordCount >= 2);
      if (!isLongEnough) return false;

      const quotedIndex = match.index || 0;
      const line = getLineForIndex(cleanResponse, quotedIndex);
      const lineWithoutQuote = line.replace(match[0], "").trim();
      const hasTooMuchContext = lineWithoutQuote.replace(/[—–\-:：]/g, "").trim().length > 8;
      if (hasTooMuchContext) return false;

      return true;
    });

    if (quotedMatches.length === 0) {
      // No usable quoted content, return original content
      return {
        description: "",
        items: [{
          title: "",
          content: cleanResponse
        }]
      };
    }

    const quotedLength = quotedMatches.reduce((sum, match) => sum + (match[1] || "").length, 0);
    const quotedDensity = quotedLength / Math.max(cleanResponse.length, 1);
    if (quotedDensity < 0.22) {
      return {
        description: "",
        items: [{
          title: "",
          content: cleanResponse
        }]
      };
    }

    const items: ResponseItem[] = [];
    let description = "";

    // If there's only one quoted content, it might be a single reply
    if (quotedMatches.length === 1) {
      const quotedContent = quotedMatches[0][1];
      const quotedIndex = quotedMatches[0].index || 0;

      // Extract content before quotes as description
      const beforeQuote = cleanResponse.substring(0, quotedIndex).trim();
      if (beforeQuote) {
        description = beforeQuote;
      }

      // Extract content after quotes as explanation
      const afterQuoteStart = quotedIndex + quotedMatches[0][0].length;
      const afterQuote = cleanResponse.substring(afterQuoteStart).trim();

      items.push({
        title: "",
        content: quotedContent,
        note: afterQuote || undefined
      });

      return { description, items };
    }

    // Multiple quoted contents, need to find corresponding title and description for each
    quotedMatches.forEach((match, index) => {
      const quotedContent = match[1];
      const quotedIndex = match.index || 0;

      // Find the nearest title before this quote
      let title = "";
      let beforeText = "";

      // Determine search range: from end of previous quote to start of current quote
      let searchStart = 0;
      if (index > 0) {
        const prevMatch = quotedMatches[index - 1];
        searchStart = (prevMatch.index || 0) + prevMatch[0].length;
      }

      const searchText = cleanResponse.substring(searchStart, quotedIndex);

      // Find title patterns: numbering + bold text
      const titlePatterns = [
        /(\d+\.\s*\*\*[^*]+\*\*)/,  // 1. **title**
        /(\*\*\d+\.\s*[^*]+\*\*)/,  // **1. title**
        /(\d+\.\s*[^"\n]+)$/m,      // 1. title (end of line)
        /(#{1,3}\s*[^#\n]+)$/m      // ### title (end of line)
      ];

      for (const pattern of titlePatterns) {
        const titleMatch = searchText.match(pattern);
        if (titleMatch) {
          title = titleMatch[1].trim();
          // Clean title format
          title = title.replace(/^\*\*|\*\*$/g, ''); // Remove bold markers
          title = title.replace(/^#+\s*/, ''); // Remove hash symbols
          break;
        }
      }

      // If no title found, use default title
      if (!title) {
        title = `${t("interface.option")} ${index + 1}`;
      }

      // Find description text after quotes (until next quote or end of text)
      let note = "";
      const afterQuoteStart = quotedIndex + match[0].length;
      let searchEnd = cleanResponse.length;

      if (index < quotedMatches.length - 1) {
        searchEnd = quotedMatches[index + 1].index || cleanResponse.length;
      }

      const afterQuoteText = cleanResponse.substring(afterQuoteStart, searchEnd).trim();

      // Extract parenthetical descriptions or other descriptive text
      const notePatterns = [
        /[（(]([^）)]+)[）)]/,  // Parenthetical content
        /^([^\n"]+)/m           // First line non-quoted content
      ];

      for (const pattern of notePatterns) {
        const noteMatch = afterQuoteText.match(pattern);
        if (noteMatch) {
          note = noteMatch[1].trim();
          break;
        }
      }

      items.push({
        title,
        content: quotedContent,
        note: note || undefined
      });
    });

    // Extract overall description (content before first quote)
    if (quotedMatches.length > 0) {
      const firstQuoteIndex = quotedMatches[0].index || 0;
      const beforeFirstQuote = cleanResponse.substring(0, firstQuoteIndex).trim();

      if (beforeFirstQuote) {
        // Clean description text, remove title markers
        let cleanDescription = beforeFirstQuote;
        cleanDescription = cleanDescription.replace(/^#+\s*[^\n]*\n?/gm, ''); // Remove title lines
        cleanDescription = cleanDescription.replace(/---+/g, ''); // Remove separators
        cleanDescription = cleanDescription.replace(/^\s*\n+|\n+\s*$/g, ''); // Remove leading/trailing empty lines

        if (cleanDescription.trim()) {
          description = cleanDescription.trim();
        }
      }
    }

    // Find bottom description (content after last quote)
    if (quotedMatches.length > 0) {
      const lastMatch = quotedMatches[quotedMatches.length - 1];
      const lastQuoteEnd = (lastMatch.index || 0) + lastMatch[0].length;
      const afterLastQuote = cleanResponse.substring(lastQuoteEnd).trim();

      // Find bottom description titles
      const bottomSectionPatterns = [
        /Selection Suggestions?[：:]?\s*\n([\s\S]*)/i,
        /Usage Suggestions?[：:]?\s*\n([\s\S]*)/i,
        /Suggestions?[：:]?\s*\n([\s\S]*)/i,
        /Notes?[：:]?\s*\n([\s\S]*)/i,
        /Instructions?[：:]?\s*\n([\s\S]*)/i,
        /选择建议[：:]?\s*\n([\s\S]*)/,
        /使用建议[：:]?\s*\n([\s\S]*)/,
        /建议[：:]?\s*\n([\s\S]*)/,
        /注意[：:]?\s*\n([\s\S]*)/,
        /说明[：:]?\s*\n([\s\S]*)/
      ];

      for (const pattern of bottomSectionPatterns) {
        const match = afterLastQuote.match(pattern);
        if (match) {
          items.push({
            title: "Usage Suggestions",
            content: match[1].trim()
          });
          break;
        }
      }
    }

    return {
      description: description || t("interface.multipleOptions"),
      items
    };
  };

  // Copy single response content
  const copyItemToClipboard = (text: string) => {
    try {
      navigator.clipboard.writeText(text).then(() => {
        toast({
          title: t("status.copied"),
          description: t("status.copyDescription"),
        });
      }).catch((error) => {
        console.warn('Copy failed, using fallback method:', error);
        // Fallback copy method
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          toast({
            title: t("status.copied"),
            description: t("status.copyDescription"),
          });
        } catch (fallbackError) {
          console.error('Fallback copy method also failed:', fallbackError);
        } finally {
          document.body.removeChild(textArea);
        }
      });
    } catch (error) {
      console.error('Copy operation failed:', error);
    }
  };

  // Copy all content
  const copyToClipboard = () => {
    let textToCopy = ""

    if (parsedResponse.items.length > 1) {
      // If multiple options, copy all options (exclude "Usage Suggestions")
      textToCopy = parsedResponse.items
        .filter(item => item.title !== "Usage Suggestions")
        .map(item => item.content)
        .join("\n\n")
    } else if (parsedResponse.items.length === 1) {
      // Single response
      textToCopy = parsedResponse.items[0].content
    }

    // If there's translation, add translation content
    if (finalTranslation) {
      textToCopy += "\n\n" + finalTranslation
    }

    try {
      navigator.clipboard.writeText(textToCopy).then(() => {
        toast({
          title: t("status.copied"),
          description: finalTranslation ? t("status.copyWithTranslationDescription") : t("status.copyDescription"),
        });
      }).catch((error) => {
        console.warn('Copy failed, using fallback method:', error);
        // Fallback copy method
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          toast({
            title: t("status.copied"),
            description: finalTranslation ? t("status.copyWithTranslationDescription") : t("status.copyDescription"),
          });
        } catch (fallbackError) {
          console.error('Fallback copy method also failed:', fallbackError);
        } finally {
          document.body.removeChild(textArea);
        }
      });
    } catch (error) {
      console.error('Copy operation failed:', error);
    }
  }

  // Fallback mechanism: ensure content is always displayed
  const ensureContent = (parsedResponse: ParsedResponse, originalResponse: string): ParsedResponse => {
    // If parsing result is empty or all item contents are empty
    if (!parsedResponse.items.length || parsedResponse.items.every(item => !item.content.trim())) {
      // If original response has content, use original content as fallback
      if (originalResponse.trim()) {
        return {
          description: t("interface.responseDescription"),
          items: [{
            title: "",
            content: originalResponse.trim()
          }]
        };
      }
      // If even original content is empty, return default message
      return {
        description: "",
        items: [{
          title: "",
          content: t("interface.noContent") || "No content available"
        }]
      };
    }

    // If there are parsing results but content might be incomplete, ensure each item has content
    const validItems = parsedResponse.items.filter(item => item.content.trim());
    if (validItems.length === 0 && originalResponse.trim()) {
      // If no valid items after filtering but original content exists, use original content
      return {
        description: parsedResponse.description || t("interface.responseDescription"),
        items: [{
          title: "",
          content: originalResponse.trim()
        }]
      };
    }

    return {
      ...parsedResponse,
      items: validItems.length > 0 ? validItems : parsedResponse.items
    };
  };

  // Parse main content and apply fallback mechanism
  const rawParsedResponse = parseResponse(mainContent);
  const parsedResponse = ensureContent(rawParsedResponse, mainContent);

  // Check if content should be displayed - now always has content
  const hasContent = parsedResponse.items.length > 0 && parsedResponse.items.some(item => item.content.trim())

  // Should not return empty now, but keep as final safety check
  if (!hasContent) {
    return (
      <Card className="glass dark:glass-dark shadow-xl border-0 bg-white/95 dark:bg-zinc-900/95">
        <CardContent className="p-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600 dark:text-indigo-400" />
              <h3 className="text-lg font-semibold text-readable">AI-Generated Response</h3>
            </div>
            <Dialog open={isOriginalOpen} onOpenChange={setIsOriginalOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="professional-button border-slate-200 dark:border-zinc-700 hover:bg-slate-50 dark:hover:bg-zinc-800 text-gray-700 dark:text-gray-300 rounded-xl"
                >
                  {t("interface.viewOriginal")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{t("interface.originalMessage")}</DialogTitle>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-auto rounded-lg bg-slate-50 dark:bg-zinc-900/50 p-4 border border-slate-200 dark:border-zinc-700">
                  <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200">
                    {response.trim() || t("interface.noContent")}
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-slate-50 dark:bg-zinc-800/50 rounded-xl p-6 border border-slate-200 dark:border-zinc-700">
            <p className="text-gray-600 dark:text-gray-400">
              {response.trim() || t("interface.noContent") || "No content available"}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass dark:glass-dark shadow-xl border-0 bg-white/95 dark:bg-zinc-900/95">
      <CardContent className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600 dark:text-indigo-400" />
            <h3 className="text-lg font-semibold text-readable">AI-Generated Response</h3>
          </div>
          <Dialog open={isOriginalOpen} onOpenChange={setIsOriginalOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="professional-button border-slate-200 dark:border-zinc-700 hover:bg-slate-50 dark:hover:bg-zinc-800 text-gray-700 dark:text-gray-300 rounded-xl"
              >
                {t("interface.viewOriginal")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{t("interface.originalMessage")}</DialogTitle>
              </DialogHeader>
              <div className="max-h-[60vh] overflow-auto rounded-lg bg-slate-50 dark:bg-zinc-900/50 p-4 border border-slate-200 dark:border-zinc-700">
                <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200">
                  {response.trim() || t("interface.noContent")}
                </p>
              </div>
            </DialogContent>
          </Dialog>
          {/* 
          <Button 
            variant="outline" 
            size="sm" 
            onClick={copyToClipboard}
            className="professional-button border-slate-200 dark:border-zinc-700 hover:bg-slate-50 dark:hover:bg-zinc-800 text-gray-700 dark:text-gray-300 rounded-xl"
          >
            <Copy className="h-4 w-4 mr-2" />
            {t("interface.copy")}
          </Button>
           */}
        </div>

        <div className="space-y-4">
          {/* Display description text (if any) */}
          {parsedResponse.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {parsedResponse.description}
            </p>
          )}

          {/* Multiple option response mode */}
          {/* parsedResponse.items.length > 1 ? ( */}
          <div className="space-y-4">
            {parsedResponse.items.map((item, index) => (
              <div key={index} className="space-y-2">
                {item.title && (
                  <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {item.title}
                  </h4>
                )}

                {/* If it's "Usage Suggestions", display as plain text */}
                {item.title === "Usage Suggestions" ? (
                  <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-4 border border-amber-200 dark:border-amber-800/50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-amber-500 dark:bg-amber-400 rounded-full"></div>
                      <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                        Usage Suggestions
                      </span>
                    </div>
                    <div className="whitespace-pre-wrap text-amber-800 dark:text-amber-200 leading-relaxed text-sm">
                      {item.content}
                    </div>
                  </div>
                ) : (
                  /* Normal options displayed as copyable text boxes */
                  <div className="relative bg-card-color rounded-xl p-6 border border-slate-200 dark:border-zinc-700 group">
                    <p className="whitespace-pre-wrap text-readable leading-relaxed">
                      {item.content}
                    </p>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyItemToClipboard(item.content)}
                        className="professional-button border-slate-200 dark:border-zinc-700 hover:bg-slate-50 dark:hover:bg-zinc-800 text-gray-700 dark:text-gray-300 rounded-xl"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                      <ShareButton
                        text={item.content}
                        className="professional-button border-slate-200 dark:border-zinc-700 hover:bg-slate-50 dark:hover:bg-zinc-800 text-gray-700 dark:text-gray-300 rounded-xl"
                        size="sm"
                        variant="outline"
                      />
                    </div>
                  </div>
                )}

                {/* Display description text (if any) */}
                {item.note && item.title !== "Usage Suggestions" && (
                  <div className="ml-4 text-xs text-gray-500 dark:text-gray-400 italic">
                    {item.note}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Translation section */}
          {finalTranslation && (
            <div className="bg-blue-50 dark:bg-indigo-950/30 rounded-xl p-6 border border-blue-200 dark:border-indigo-800/50">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 bg-blue-500 dark:bg-indigo-400 rounded-full"></div>
                <span className="text-sm font-medium text-blue-700 dark:text-indigo-300">
                  {t("interface.translation")} ( {currentLanguage.toUpperCase()} )
                </span>
              </div>
              <p className="whitespace-pre-wrap text-blue-800 dark:text-indigo-200 leading-relaxed">{finalTranslation}</p>
            </div>
          )}
        </div>

        <div className="mt-4 p-3 bg-green-50 dark:bg-emerald-950/30 rounded-xl border border-green-200 dark:border-emerald-800/50">
          <p className="text-sm text-green-700 dark:text-emerald-300">
            ✓ {finalTranslation ? t("status.successWithTranslation") : t("status.success")}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}